import { BIBLIO } from '@somnhold/content';
import { DIRS, hexLen, hexToWorld, ROW, type Hex } from '../hex.ts';
import { idiv, imax, isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';

/**
 * The burnt library of the Order (docs/sendas/mago.md, chapter 1): the grounds, the same from the seed in the sim and
 * the drawing, integers only. Up is −y (the top of the screen).
 *
 * - `sootLayout` (act 1, the burnt cellars): four halls one below the other, each nine hexes wide and five rows tall,
 *   joined by a stair two hexes wide at alternate ends; in each, piles of ash where the risen sleep, a fallen shelf
 *   across its middle, burning shelves on its side walls; the catalog's wall of drawers at the last hall's far end.
 * - `lecternLayout` (act 2, the three locks): a round room; three iron lecterns across it, each with its frost bowl, acid
 *   flask and brazier in an arc before it; the door at the top where the scribes come in.
 * - `cabinetLayout` (act 3, the test): the fire room, round; Ansel's iron cabinet in its middle (three hexes across),
 *   six spokes of floor from it to the wall where the walls of fire rise, three shelves in alternate wedges between them
 *   (each with its bowl, flask and brazier, facing one seal of the cabinet), and a door in each of the other three.
 */

export const SOOT_RADIUS = 14;
export const LECTERN_RADIUS = 8;
export const CABINET_RADIUS = 9;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const key = (h: Hex): string => `${h.q},${h.r}`;

/** The hex on row `r` whose centre is nearest world x. */
const nearestOn = (x: number, r: number): Hex => {
  const v = x - r * 500 + 500;
  return { q: (v - (((v % 1000) + 1000) % 1000)) / 1000, r };
};

const rocksOf = (R: number, open: Set<string>): { rocks: Hex[]; floor: Hex[] } => {
  const rocks: Hex[] = [];
  const floor: Hex[] = [];
  for (const h of allCells(R)) (open.has(key(h)) ? floor : rocks).push(h);
  return { rocks: rocks.sort(sortHex), floor: floor.sort(sortHex) };
};

/* ---------- the burnt cellars ---------- */

const SOOT_ROWS = [-9, -3, 3, 9];
const SOOT_HALF_ROWS = 2;
const SOOT_HALF_W = 4500;

export interface SootHall {
  at: { x: number; y: number };
  cells: Hex[];
  in: { x: number; y: number };
  out: { x: number; y: number };
  /** The stair's two cells down out of it (its fallen beams): none for the last. */
  stair: Hex[];
  /** Piles of ash where the risen sleep: 0 a skeleton, 1 an apparition. */
  piles: { x: number; y: number; kind: 0 | 1 }[];
  /** The fallen shelf across it (rock cells). */
  fallen: Hex[];
  /** Its burning shelves on the side walls (the pages come off them). */
  shelves: { x: number; y: number }[];
}

export interface SootLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  halls: SootHall[];
  start: { x: number; y: number };
  /** The catalog's wall of drawers, at the last hall's far end. */
  catalog: { x: number; y: number };
}

/** The burnt cellars from the seed. */
export const sootLayout = (seed: number): SootLayout => {
  const R = SOOT_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xb1b1));
  const east0 = rngInt(rng, 2) === 0;
  const open = new Set<string>();
  const halls: SootHall[] = [];
  SOOT_ROWS.forEach((row, i) => {
    const cx = rngRange(rng, -1, 1) * 600;
    const cells: Hex[] = [];
    for (let r = row - SOOT_HALF_ROWS; r <= row + SOOT_HALF_ROWS; r++) {
      // (A little rounder at the top and bottom rows, but never off the stairs in and out at the ends.)
      const half = Math.abs(r - row) === SOOT_HALF_ROWS ? SOOT_HALF_W - 400 : SOOT_HALF_W;
      for (let q = -R; q <= R; q++) {
        if (hexLen(q, r) >= R) continue;
        const p = hexToWorld(q, r);
        if (p.x < cx - half || p.x > cx + half) continue;
        cells.push({ q, r });
        open.add(key({ q, r }));
      }
    }
    const east = (i % 2 === 0) === east0;
    const prev = halls[i - 1];
    const inX = prev ? prev.out.x : cx + (east ? -1 : 1) * (SOOT_HALF_W - 1500);
    const outX = cx + (east ? 1 : -1) * (SOOT_HALF_W - 1500);
    halls.push({
      at: { x: cx, y: row * ROW },
      cells: cells.sort(sortHex),
      in: { x: inX, y: (row - SOOT_HALF_ROWS) * ROW },
      out: { x: outX, y: (row + SOOT_HALF_ROWS) * ROW },
      stair: [],
      piles: [],
      fallen: [],
      shelves: [
        { x: cx - SOOT_HALF_W - 300, y: row * ROW - 400 },
        { x: cx + SOOT_HALF_W + 300, y: row * ROW + 400 },
      ],
    });
  });
  for (let i = 0; i < halls.length - 1; i++) {
    const a = halls[i] as SootHall;
    const row = (SOOT_ROWS[i] as number) + SOOT_HALF_ROWS + 1;
    const h0 = nearestOn(a.out.x, row);
    const toward = a.out.x > a.at.x ? -1 : 1;
    a.stair = [h0, { q: h0.q + toward, r: row }].sort(sortHex);
    for (const h of a.stair) open.add(key(h));
  }
  // A fallen shelf across each hall but the first (two cells on its middle row, off its middle), the piles spread
  // across its far half (away from its way in) and clear of the shelf.
  halls.forEach((h, i) => {
    const row = SOOT_ROWS[i] as number;
    if (i > 0) {
      const side = rngInt(rng, 2) === 0 ? -1 : 1;
      const f0 = nearestOn(h.at.x + side * 1200, row);
      h.fallen = [f0, { q: f0.q + side, r: row }].sort(sortHex);
      for (const c of h.fallen) open.delete(key(c));
    }
    const n = BIBLIO.soot.piles[i] as number;
    const spots = h.cells
      .filter((c) => Math.abs(c.r - row) <= 1 && !h.fallen.some((f) => f.q === c.q && f.r === c.r))
      .map((c) => hexToWorld(c.q, c.r))
      .filter((p) => Math.abs(p.x - h.in.x) + Math.abs(p.y - h.in.y) > 2400 && !h.fallen.some((f) => Math.abs(hexToWorld(f.q, f.r).x - p.x) + Math.abs(hexToWorld(f.q, f.r).y - p.y) < 1400));
    const all = spots.slice();
    for (let k = 0; k < n && all.length; k++) {
      // (Spread out while there's room; else any spot not taken.)
      const pool = spots.length ? spots : all.filter((o) => !h.piles.some((q) => q.x === o.x && q.y === o.y));
      if (!pool.length) break;
      const p = pool.splice(rngInt(rng, pool.length), 1)[0] as {
        x: number;
        y: number;
      };
      h.piles.push({
        x: p.x,
        y: p.y,
        kind: (k + 1) % BIBLIO.soot.wraithEvery === 0 ? 1 : 0,
      });
      // (Two piles never side by side.)
      for (let j = spots.length - 1; j >= 0; j--) {
        const o = spots[j] as { x: number; y: number };
        if (Math.abs(o.x - p.x) + Math.abs(o.y - p.y) < 1400) spots.splice(j, 1);
      }
    }
  });
  const { rocks, floor } = rocksOf(R, open);
  const first = halls[0] as SootHall;
  const last = halls[halls.length - 1] as SootHall;
  // (The catalog's wall at the last hall's far end: the floor cell a row above its bottom edge, under the drawers.)
  const cat = nearestOn(last.out.x, (SOOT_ROWS[SOOT_ROWS.length - 1] as number) + 1);
  return {
    radius: R,
    rocks,
    floor,
    halls,
    start: { x: first.in.x, y: first.in.y + ROW },
    catalog: hexToWorld(cat.q, cat.r),
  };
};

/* ---------- the three lecterns ---------- */

export interface LecternLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** Each lectern: its cell, its point, its seal's kind (0 frost, 1 crust, 2 ink). */
  lecterns: { hex: Hex; x: number; y: number; kind: 0 | 1 | 2 }[];
  /** The items in an arc before each lectern: its kind (0 bowl, 1 flask, 2 brazier), where, and its lectern. */
  items: { kind: 0 | 1 | 2; x: number; y: number; seal: number }[];
  door: { x: number; y: number };
  /** Where the pages come in from (the side walls' shelves). */
  shelves: { x: number; y: number }[];
  start: { x: number; y: number };
}

/** A shuffle of 0, 1, 2 from the PRNG. */
const three = (rng: ReturnType<typeof seedRng>): [0 | 1 | 2, 0 | 1 | 2, 0 | 1 | 2] => {
  const a: (0 | 1 | 2)[] = [0, 1, 2];
  for (let i = 2; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = a[i] as 0 | 1 | 2;
    a[i] = a[j] as 0 | 1 | 2;
    a[j] = t;
  }
  return a as [0 | 1 | 2, 0 | 1 | 2, 0 | 1 | 2];
};

export const lecternLayout = (seed: number): LecternLayout => {
  const R = LECTERN_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x1ec7));
  const open = new Set<string>();
  for (const h of allCells(R - 1)) open.add(key(h));
  const kinds = three(rng);
  const lecterns = [-3800, 0, 3800].map((x, i) => {
    const hex = nearestOn(x, -1);
    const p = hexToWorld(hex.q, hex.r);
    open.delete(key(hex));
    return { hex, x: p.x, y: p.y, kind: kinds[i] as 0 | 1 | 2 };
  });
  const items: LecternLayout['items'] = [];
  lecterns.forEach((l, i) => {
    const order = three(rng);
    [-1250, 0, 1250].forEach((dx, k) =>
      items.push({
        kind: order[k] as 0 | 1 | 2,
        x: l.x + dx,
        y: l.y + (k === 1 ? 1800 : 1300),
        seal: i,
      }),
    );
  });
  const { rocks, floor } = rocksOf(R, open);
  return {
    radius: R,
    rocks,
    floor,
    lecterns,
    items,
    door: { x: 0, y: -(R - 1) * ROW },
    shelves: [
      { x: -6300, y: 0 },
      { x: 6300, y: 0 },
    ],
    start: { x: 0, y: 5 * ROW },
  };
};

/* ---------- the fire room ---------- */

export interface CabinetLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** The cabinet's cells (the middle and its ring). */
  cabinet: Hex[];
  /** Each wall of fire's cells, out from the cabinet to the wall, in their turn (the first rises first). */
  spokes: Hex[][];
  /** The way the walls of fire go round (1 the hexes' order, −1 the other). */
  turn: 1 | -1;
  /** Each shelf: its middle by the wall, the seal of the cabinet facing it (its point and kind), and its three items. */
  shelves: {
    x: number;
    y: number;
    seal: { x: number; y: number; kind: 0 | 1 | 2 };
    items: { kind: 0 | 1 | 2; x: number; y: number }[];
  }[];
  /** The doors in the wall where the scribes and the pages come in. */
  doors: { x: number; y: number }[];
  start: { x: number; y: number };
}

/** A wedge's middle direction (between spoke k and k + 1), at `d` from the middle (sim units). */
const wedgeAt = (k: number, d: number): { x: number; y: number } => {
  const a = DIRS[k % 6] as Hex;
  const b = DIRS[(k + 1) % 6] as Hex;
  const pa = hexToWorld(a.q, a.r);
  const pb = hexToWorld(b.q, b.r);
  const x = pa.x + pb.x;
  const y = pa.y + pb.y;
  const l = imax(1, isqrt(x * x + y * y));
  return { x: idiv(x * d, l), y: idiv(y * d, l) };
};

export const cabinetLayout = (seed: number): CabinetLayout => {
  const R = CABINET_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xf1e0));
  const open = new Set<string>();
  for (const h of allCells(R - 1)) if (hexLen(h.q, h.r) > 1) open.add(key(h));
  const cabinet = allCells(1).sort(sortHex);
  // The spokes along the hex directions, from ring 2 to the wall.
  const spokesBy: Hex[][] = DIRS.map((d) => {
    const out: Hex[] = [];
    for (let n = 2; n <= R - 1; n++) out.push({ q: d.q * n, r: d.r * n });
    return out.sort(sortHex);
  });
  const start0 = rngInt(rng, 6);
  const turn: 1 | -1 = rngInt(rng, 2) === 0 ? 1 : -1;
  const spokes = [0, 1, 2, 3, 4, 5].map((k) => spokesBy[(((start0 + turn * k) % 6) + 6) % 6] as Hex[]);
  // Shelves in alternate wedges (from the seed), doors in the others.
  const odd = rngInt(rng, 2);
  const kinds = three(rng);
  const shelves: CabinetLayout['shelves'] = [];
  const doors: CabinetLayout['doors'] = [];
  for (let k = 0; k < 6; k++) {
    if (k % 2 !== odd) {
      doors.push(wedgeAt(k, (R - 1) * 1000 - 200));
      continue;
    }
    const mid = wedgeAt(k, 6300);
    const seal = wedgeAt(k, 1450);
    // Across the wedge (its perpendicular), a hex and a bit each side.
    const l = imax(1, isqrt(mid.x * mid.x + mid.y * mid.y));
    const px = idiv(-mid.y * 1150, l);
    const py = idiv(mid.x * 1150, l);
    const order = three(rng);
    const at = [
      { x: mid.x - px, y: mid.y - py },
      { x: mid.x + idiv(mid.x * 350, l), y: mid.y + idiv(mid.y * 350, l) },
      { x: mid.x + px, y: mid.y + py },
    ];
    shelves.push({
      x: mid.x,
      y: mid.y,
      seal: { ...seal, kind: kinds[shelves.length] as 0 | 1 | 2 },
      items: at.map((p, i) => ({
        kind: order[i] as 0 | 1 | 2,
        x: p.x,
        y: p.y,
      })),
    });
  }
  const { rocks, floor } = rocksOf(R, open);
  const d0 = doors[0] as { x: number; y: number };
  return {
    radius: R,
    rocks,
    floor,
    cabinet,
    spokes,
    turn,
    shelves,
    doors,
    start: { x: idiv(d0.x * 5, 8), y: idiv(d0.y * 5, 8) },
  };
};
